/**
 * The live tail: accumulating a packet log across polls.
 *
 * `GET /messages` returns a page, not a stream, so something has to remember
 * what came before. That is this module, kept pure and clock-free so all of it
 * is testable — which matters, because every one of the four edge cases below
 * has an obvious answer that is wrong.
 *
 * The cursor is `seq`, never `receivedAt`. Two messages can share a millisecond
 * (a single send on the dev channel table produces an outbound and an inbound
 * record with identical timestamps), and a timestamp cursor silently drops one
 * of them. `seq` is monotonic within a process and has no such gap.
 */
import type { Hl7MessageFilter, Hl7MessageList, Hl7MessageSummary } from './types'

/** Matches the harness's default MESSAGE_STORE_SIZE; holding more than it keeps is pointless. */
export const MAX_BUFFER = 500

export interface TailState {
  /** Newest first, deduped by seq, capped at MAX_BUFFER. */
  messages: Hl7MessageSummary[]
  /** Highest seq ever applied. 0 means no cursor yet, so the next request omits sinceSeq. */
  cursor: number
  /** Which filter this buffer belongs to. A change invalidates the cursor entirely. */
  filterKey: string
  /** Rows the server matched but a page could not carry. Recoverable by reloading. */
  missed: number
  /** Set for the tick that detected a seq rewind, so the UI can say why it emptied. */
  restarted: boolean
  /** Last uptime seen, for the restart detector. Null until status has been read once. */
  lastUptimeSec: number | null
}

/** Stable and order-independent, so an equal filter always produces an equal key. */
export function tailFilterKey(filter: Hl7MessageFilter): string {
  return JSON.stringify({
    direction: filter.direction ?? null,
    channel: filter.channel ?? null,
    type: filter.type ?? null,
  })
}

export function emptyTail(filterKey: string): TailState {
  return { messages: [], cursor: 0, filterKey, missed: 0, restarted: false, lastUptimeSec: null }
}

/** What to ask for next. Omits the cursor until there is one. */
export function nextRequest(state: TailState): { sinceSeq?: number } {
  return state.cursor > 0 ? { sinceSeq: state.cursor } : {}
}

/**
 * Clear emptied the display, not the run.
 *
 * `DELETE /messages` empties the store but deliberately does NOT reset `seq`
 * (see `store.clear()` in the harness), so the cursor stays valid and everything
 * that arrives afterwards tails in normally. Re-priming instead would be the
 * obvious move and would fetch nothing at all.
 */
export function clearTail(state: TailState): TailState {
  return { ...state, messages: [], missed: 0, restarted: false }
}

/**
 * A filter change invalidates the cursor.
 *
 * A message with a LOWER seq can match the new filter while never having
 * matched the old one, so tailing from the retained cursor would hide it
 * permanently. Returns the same reference when the key is unchanged, so this is
 * safe to call on every tick.
 */
export function resetIfFilterChanged(state: TailState, filterKey: string): TailState {
  return state.filterKey === filterKey ? state : emptyTail(filterKey)
}

/**
 * Fold a page into the buffer. Pure, clock-free, and idempotent: applying the
 * same page twice is indistinguishable from applying it once, which is what
 * makes this safe under StrictMode's double invocation.
 */
export function applyPage(
  state: TailState,
  page: Hl7MessageList,
  context: { uptimeSec: number | null } = { uptimeSec: null },
): TailState {
  const { uptimeSec } = context

  // A restart rewinds seq to 0, and then `?sinceSeq=<old cursor>` matches
  // nothing ever again — the screen looks alive and shows nothing, forever.
  //
  // Uptime going backwards is the only sound detector. "a row arrived at or
  // below the cursor" looks like a second one, and is not: after a page is
  // applied the cursor sits past every row in it, so re-applying that same page
  // — precisely what StrictMode's double invocation does — would read as a
  // rewind and wipe the buffer. Status is polled on the same tick as messages,
  // so uptime is available in practice; the Refresh button is the backstop when
  // it is not.
  const uptimeRewound =
    uptimeSec !== null && state.lastUptimeSec !== null && uptimeSec < state.lastUptimeSec

  if (uptimeRewound) {
    // Discard this page too: it was fetched against a cursor that no longer
    // means anything. The next tick refills from scratch.
    return { ...emptyTail(state.filterKey), restarted: true, lastUptimeSec: uptimeSec }
  }

  const withUptime = uptimeSec !== null && uptimeSec !== state.lastUptimeSec

  if (page.messages.length === 0) {
    const missed = state.missed + Math.max(0, page.total - page.returned)
    // Identity when genuinely nothing changed, so structural sharing keeps the
    // list from re-rendering on every idle tick.
    if (missed === state.missed && !state.restarted && !withUptime) return state
    return { ...state, missed, restarted: false, lastUptimeSec: uptimeSec ?? state.lastUptimeSec }
  }

  // Keyed by seq, so duplicates and out-of-order pages both fold correctly and
  // re-applying a page overwrites rather than appends.
  const merged = new Map<number, Hl7MessageSummary>()
  for (const message of state.messages) merged.set(message.seq, message)
  for (const message of page.messages) merged.set(message.seq, message)

  const messages = [...merged.values()].sort((a, b) => b.seq - a.seq).slice(0, MAX_BUFFER)
  const highest = page.messages.reduce((max, m) => Math.max(max, m.seq), state.cursor)

  return {
    messages,
    cursor: highest,
    filterKey: state.filterKey,
    missed: state.missed + Math.max(0, page.total - page.returned),
    restarted: false,
    lastUptimeSec: uptimeSec ?? state.lastUptimeSec,
  }
}
